import type { Immutable } from "immer";
import React from "react";
import { getGlobalSpacingClasses } from "tsi-common-react/src/components/_spacing/globalSpacing";

import type { SplitKingCalloutBlock as SplitKingCalloutBlockValue } from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

/**
 * Faithful port of the legacy server-rendered
 * ``cms/blocks/split-king-callout-block.html``.
 *
 * ``header_content`` was rendered via ``{% include_block value.header_content %}``
 * (a raw RichText value), which emits the BARE expanded HTML with NO
 * ``.rich-text`` wrapper. The ``.split-king-callout-block > .copy .rich-text``
 * SCSS rule was therefore dead in the legacy render, so we deliberately do NOT
 * add ``className="rich-text"`` here (matching the AwardFeature/AwardColumns
 * port convention -- adding it would activate the otherwise-dead
 * ``text-align: center`` rule and shift the layout).
 *
 * Image: the legacy ``<picture>`` rendered three renditions -- a mobile
 * ``fill-450x777-c100`` source, a tablet ``fill-960x1200-c25`` source, and a
 * ``width-1600`` default ``<img>`` (alt = the image title; the legacy
 * ``django_image image.url alt=image.alt`` resolves to the rendition's default
 * alt, i.e. the image title -- matching the ProductCallout port). The whole
 * ``<picture>`` only renders when an image is set.
 */
export const SplitKingCalloutBlock = (
    props: Immutable<{ value: SplitKingCalloutBlockValue }>,
): React.JSX.Element => {
    const v = props.value;
    const image = v.image;
    return (
        <section
            className={concatClassNames([
                "split-king-callout-block",
                getGlobalSpacingClasses(v.spacing),
            ])}
        >
            <div className="copy">
                <RichTextBlock value={v.header_content ?? ""} />
                <div className="callouts">
                    <div>
                        <h4>Side One</h4>
                        <ul>
                            <li>Soft</li>
                            <li className="orient">
                                <em>Med</em>
                            </li>
                            <li>Firm</li>
                        </ul>
                    </div>
                    <div>
                        <h4>Side Two</h4>
                        <ul>
                            <li>Soft</li>
                            <li>Med</li>
                            <li className="rose-of-sharon">
                                <em>Firm</em>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
            {image ? (
                <picture>
                    <source
                        media="(max-width: 578px)"
                        srcSet={image.renditions["fill-450x777-c100"]?.url}
                    />
                    <source
                        media="(min-width: 579px) and (max-width: 960px)"
                        srcSet={image.renditions["fill-960x1200-c25"]?.url}
                    />
                    <img
                        src={image.renditions["width-1600"]?.url ?? image.url}
                        alt={image.title}
                        loading="lazy"
                        decoding="async"
                    />
                </picture>
            ) : null}
        </section>
    );
};
